/**
 * Self-hosted font imports via @fontsource.
 *
 * The set of imports mirrors `packages/styles/fonts/catalog.json` — every
 * font in the catalog must be importable here so users can switch to any of
 * them without a network round-trip. Adding a font to the catalog without
 * adding the import here means the font will fall through to the system
 * fallback.
 *
 * The actual .woff2 files are only downloaded by the browser when text using
 * that font family is rendered. A preload pass runs at idle so that switching
 * fonts in the picker is instant with no FOUT.
 */

import { fontCatalog } from "@caliu/styles/fonts";

// ── @fontsource imports ─────────────────────────────────────────
// Sans
import "@fontsource-variable/plus-jakarta-sans";
import "@fontsource-variable/plus-jakarta-sans/wght-italic.css";
import "@fontsource-variable/source-sans-3";
import "@fontsource-variable/inter-tight";
import "@fontsource-variable/figtree";
import "@fontsource-variable/nunito-sans";
import "@fontsource-variable/manrope";
import "@fontsource-variable/dm-sans";
import "@fontsource-variable/dm-sans/wght-italic.css";
import "@fontsource-variable/instrument-sans";
import "@fontsource-variable/space-grotesk";
import "@fontsource-variable/sora";

// Serif
import "@fontsource-variable/lora";
import "@fontsource-variable/lora/wght-italic.css";
import "@fontsource-variable/crimson-pro";
import "@fontsource-variable/crimson-pro/wght-italic.css";
import "@fontsource-variable/libre-baskerville";
import "@fontsource/spectral/400.css";
import "@fontsource/spectral/400-italic.css";
import "@fontsource/spectral/500.css";
import "@fontsource/spectral/600.css";
import "@fontsource-variable/source-serif-4";
import "@fontsource-variable/source-serif-4/wght-italic.css";
import "@fontsource-variable/alegreya";
import "@fontsource-variable/alegreya/wght-italic.css";
import "@fontsource-variable/fraunces";
import "@fontsource-variable/fraunces/wght-italic.css";
import "@fontsource-variable/newsreader";
import "@fontsource-variable/newsreader/wght-italic.css";
import "@fontsource-variable/bitter";
import "@fontsource-variable/bitter/wght-italic.css";

// Mono
import "@fontsource-variable/jetbrains-mono";
import "@fontsource-variable/inconsolata";
import "@fontsource/ubuntu-mono/400.css";
import "@fontsource/ubuntu-mono/700.css";
import "@fontsource/space-mono/400.css";
import "@fontsource/space-mono/700.css";
import "@fontsource-variable/red-hat-mono";
import "@fontsource-variable/source-code-pro";
import "@fontsource/dm-mono/400.css";
import "@fontsource/dm-mono/500.css";
import "@fontsource-variable/azeret-mono";
import "@fontsource/ibm-plex-mono/400.css";
import "@fontsource/ibm-plex-mono/500.css";
import "@fontsource-variable/fira-code";

// ──────────────────────────────────────────────────────────────────
// Font preloading
// ──────────────────────────────────────────────────────────────────

function loadFamilyName(entry: { family: string; fontsourceVariant: string }): string {
  // @fontsource-variable packages register the family as "X Variable".
  // Static @fontsource packages register the bare family name.
  return entry.fontsourceVariant === "variable" ? `${entry.family} Variable` : entry.family;
}

/**
 * Trigger woff2 downloads for every catalog font so that switching fonts
 * is instant (no FOUT). Called once on app startup via requestIdleCallback.
 */
function preloadAllCatalogFonts(): void {
  const loads: Promise<FontFace[]>[] = [];

  for (const entry of fontCatalog) {
    if (entry.system) continue;
    const loadFamily = loadFamilyName(entry);
    loads.push(document.fonts.load(`400 16px "${loadFamily}"`));
    if (entry.fontsourceItalic) {
      loads.push(document.fonts.load(`italic 400 16px "${loadFamily}"`));
    }
  }

  Promise.allSettled(loads);
}

// Preload all fonts after the browser is idle so font switches are instant.
if (typeof requestIdleCallback === "function") {
  requestIdleCallback(() => preloadAllCatalogFonts());
} else {
  setTimeout(preloadAllCatalogFonts, 2000);
}
